import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button, Spinner } from "react-bootstrap";
import { formatDistance } from "date-fns";
import pt from "date-fns/locale/pt-BR";
import Swal from "sweetalert2";
import {
  getAutomations,
  toggleAutomationActive,
  deleteAutomation,
  duplicateAutomation,
} from "./services/AutomationServices";
import {
  IconPlus,
  IconPencil,
  IconTrash,
  IconCopy,
  IconPower,
  IconSearch,
  IconFilter,
} from "@tabler/icons-react";
import styled from "styled-components";

// Importar contexto do AgentHub (opcional)
import { useAgentHubContext } from '../AgentHub/contexts/AgentHubContext';

// Styled Components
const Container = styled.div`
  margin: 0;
  background: ${props => props.isInsideAgentHub ? 'transparent' : '#f8f9fa'};

  @media (max-width: 768px) {
    background: ${props => props.isInsideAgentHub ? 'transparent' : '#ffffff'};
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }
`;

const Title = styled.h2`
  color: #212529;
  font-weight: 700;
  font-size: 28px;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 24px;
    text-align: center;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 400px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 48px;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  font-size: 16px;
  background: white;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }

  &::placeholder {
    color: #6c757d;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const FilterButton = styled.button`
  background: ${(props) => (props.active ? "#007bff" : "white")};
  color: ${(props) => (props.active ? "white" : "#495057")};
  border: 2px solid ${(props) => (props.active ? "#007bff" : "#dee2e6")};
  border-radius: 8px;
  padding: 8px 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;

  &:hover {
    background: ${(props) => (props.active ? "#0056b3" : "#e9ecef")};
    border-color: ${(props) => (props.active ? "#0056b3" : "#adb5bd")};
  }
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
`;

const StatCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  text-align: center;

  @media (max-width: 768px) {
    padding: 12px;
  }
`;

const StatNumber = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #212529;
  margin-bottom: 4px;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: #6c757d;
  text-transform: uppercase;
  font-weight: 500;
`;

const CardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
  margin-bottom: 80px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
    margin-bottom: 100px;
  }
`;

const AutomationCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  cursor: pointer;
  border: 2px solid transparent;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    border-color: #007bff;
  }

  &:active {
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    border-radius: 12px;
    padding: 16px;

    &:hover {
      transform: none;
    }

    &:active {
      background: #f8f9fa;
    }
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const CardTitle = styled.h3`
  color: #212529;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  flex: 1;
  margin-right: 12px;
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  background: ${(props) => (props.active ? "#28a745" : "#dc3545")};
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
`;

const CardContent = styled.div`
  margin-bottom: 16px;
`;

const Description = styled.p`
  color: #6c757d;
  font-size: 14px;
  line-height: 1.4;
  margin: 0 0 12px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 14px;
`;

const InfoLabel = styled.span`
  color: #6c757d;
  font-weight: 500;
`;

const InfoValue = styled.span`
  color: #495057;
  font-weight: 500;
`;

const TypeBadge = styled.span`
  display: inline-block;
  background: ${(props) => {
    switch (props.automationType) {
      case "whatsapp":
        return "#25D366";
      case "email":
        return "#4285F4";
      case "webhook":
        return "#FF6B6B";
      case "followup":
        return "#6366f1";
      default:
        return "#25D366";
    }
  }};
  color: white;
  padding: 4px 8px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  border-top: 1px solid #e9ecef;
`;

const UpdateDate = styled.span`
  color: #6c757d;
  font-size: 12px;
`;

const ActionsContainer = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  background: ${(props) => {
    if (props.variant === "toggle") return props.active ? "#28a745" : "#6c757d";
    if (props.variant === "edit") return "#007bff";
    if (props.variant === "duplicate") return "#17a2b8";
    if (props.variant === "delete") return "#dc3545";
    return "#6c757d";
  }};
  color: white;
  border: none;
  border-radius: 8px;
  padding: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    transform: scale(1.05);
    filter: brightness(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const FloatingButton = styled.button`
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 56px;
  height: 56px;
  background: #007bff;
  color: white;
  border: none;
  border-radius: 50%;
  font-size: 24px;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0, 123, 255, 0.3);
  transition: all 0.3s ease;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #0056b3;
    transform: scale(1.05);
    box-shadow: 0 6px 20px rgba(0, 123, 255, 0.4);
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 768px) {
    bottom: 70px;
    right: 10px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #6c757d;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6c757d;

  h3 {
    color: #495057;
    margin-bottom: 8px;
  }

  p {
    margin-bottom: 24px;
  }
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.5;
`;

const AutomationsList = () => {
  const [automations, setAutomations] = useState([]);
  const [filteredAutomations, setFilteredAutomations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const navigate = useNavigate();
  const { agentId: urlAgentId } = useParams();

  // Tentar obter contexto do AgentHub (opcional)
  const agentContext = useAgentHubContext();
  const agentId = agentContext?.agentId || urlAgentId;
  // Detectar se está no AgentHub pela URL ou contexto
  const isInsideAgentHub = Boolean(agentContext) || window.location.pathname.includes('/agent-hub/');

  const fetchAutomations = async () => {
    setLoading(true);
    try {
      // Incluir agent_id se estivermos dentro do AgentHub
      const params = agentId ? { agent_id: agentId } : {};
      const data = await getAutomations(params);
      setAutomations(data.results || []);
      setFilteredAutomations(data.results || []);
    } catch (error) {
      Swal.fire({
        title: "Erro!",
        text: "Ocorreu um erro ao buscar as automações.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAutomations();
  }, [agentId]); // Re-fetch when agentId changes

  // Filter and search logic
  useEffect(() => {
    let filtered = automations;

    // Apply search filter
    if (searchTerm.trim()) {
      filtered = filtered.filter(
        (automation) =>
          automation.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (automation.description &&
            automation.description
              .toLowerCase()
              .includes(searchTerm.toLowerCase()))
      );
    }

    // Apply status filter
    if (filterStatus !== "all") {
      filtered = filtered.filter((automation) =>
        filterStatus === "active" ? automation.active : !automation.active
      );
    }

    // Apply type filter
    if (filterType !== "all") {
      filtered = filtered.filter((automation) =>
        automation.automation_type === filterType
      );
    }

    setFilteredAutomations(filtered);
  }, [automations, searchTerm, filterStatus, filterType]);

  const handleToggleActive = async (id, e) => {
    e.stopPropagation();
    try {
      const response = await toggleAutomationActive(id);
      setAutomations(
        automations.map((automation) =>
          automation.id === id
            ? { ...automation, active: response.active }
            : automation
        )
      );
      Swal.fire({
        title: "Sucesso!",
        text: `Automação ${
          response.active ? "ativada" : "desativada"
        } com sucesso!`,
        icon: "success",
        timer: 2000,
        showConfirmButton: true,
      });
    } catch (error) {
      Swal.fire({
        title: "Erro!",
        text: "Ocorreu um erro ao alterar o status da automação.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const handleDelete = (id, name, e) => {
    e.stopPropagation();
    Swal.fire({
      title: "Tem certeza?",
      text: `Deseja realmente excluir a automação "${name}"? Esta ação não pode ser desfeita.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, excluir!",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteAutomation(id);
          setAutomations(
            automations.filter((automation) => automation.id !== id)
          );
          Swal.fire({
            title: "Excluído!",
            text: "Automação excluída com sucesso.",
            icon: "success",
            timer: 2000,
            showConfirmButton: true,
          });
        } catch (error) {
          Swal.fire({
            title: "Erro!",
            text: "Ocorreu um erro ao excluir a automação.",
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      }
    });
  };

  const handleDuplicate = async (id, e) => {
    e.stopPropagation();
    try {
      const duplicated = await duplicateAutomation(id);
      setAutomations([...automations, duplicated]);
      Swal.fire({
        title: "Duplicado!",
        text: "Automação duplicada com sucesso.",
        icon: "success",
        timer: 2000,
        showConfirmButton: true,
      });
    } catch (error) {
      Swal.fire({
        title: "Erro!",
        text: "Ocorreu um erro ao duplicar a automação.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const handleEdit = (automation, e) => {
    e.stopPropagation();
    if (isInsideAgentHub) {
      navigate(`/agent-hub/${agentId}/automations/edit/${automation.id}`, {
        state: { automation: automation },
      });
    } else {
      navigate(`/automations/edit/${automation.id}`, {
        state: { automation: automation },
      });
    }
  };

  const handleCardClick = (automation) => {
    if (isInsideAgentHub) {
      navigate(`/agent-hub/${agentId}/automations/${automation.id}`);
    } else {
      navigate(`/automations/${automation.id}`);
    }
  };


  const getStats = () => {
    const total = automations.length;
    const active = automations.filter((a) => a.active).length;
    const inactive = total - active;
    const totalRules = automations.reduce(
      (acc, a) => acc + (a.rules_count || 0),
      0
    );
    const followups = automations.filter((a) => a.automation_type === 'followup').length;

    return { total, active, inactive, totalRules, followups };
  };

  const stats = getStats();

  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Carregando automações...</p>
        </LoadingContainer>
      </Container>
    );
  }

  return (
    <Container className="p-4" isInsideAgentHub={isInsideAgentHub}>
      <Header>
        <Title>{isInsideAgentHub ? 'Automações do Agente' : 'Automações'}</Title>
      </Header>

      <SearchContainer>
        <SearchIcon>
          <IconSearch size={18} />
        </SearchIcon>
        <SearchInput
          type="text"
          placeholder="Buscar automações..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </SearchContainer>

      <FilterContainer>
        <FilterButton
          active={filterStatus === "all"}
          onClick={() => setFilterStatus("all")}
        >
          Todas
        </FilterButton>
        <FilterButton
          active={filterStatus === "active"}
          onClick={() => setFilterStatus("active")}
        >
          Ativas
        </FilterButton>
        <FilterButton
          active={filterStatus === "inactive"}
          onClick={() => setFilterStatus("inactive")}
        >
          Inativas
        </FilterButton>
      </FilterContainer>

      <FilterContainer>
        <FilterButton
          active={filterType === "all"}
          onClick={() => setFilterType("all")}
        >
          Todos os Tipos
        </FilterButton>
        <FilterButton
          active={filterType === "whatsapp"}
          onClick={() => setFilterType("whatsapp")}
        >
          📱 WhatsApp
        </FilterButton>
        <FilterButton
          active={filterType === "email"}
          onClick={() => setFilterType("email")}
        >
          📧 Email
        </FilterButton>
        <FilterButton
          active={filterType === "webhook"}
          onClick={() => setFilterType("webhook")}
        >
          🔗 Webhook
        </FilterButton>
        <FilterButton
          active={filterType === "followup"}
          onClick={() => setFilterType("followup")}
        >
          🤖 Follow-up
        </FilterButton>
      </FilterContainer>

      <StatsContainer>
        <StatCard>
          <StatNumber>{stats.total}</StatNumber>
          <StatLabel>Total</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{stats.active}</StatNumber>
          <StatLabel>Ativas</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{stats.inactive}</StatNumber>
          <StatLabel>Inativas</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{stats.totalRules}</StatNumber>
          <StatLabel>Regras</StatLabel>
        </StatCard>
        <StatCard>
          <StatNumber>{stats.followups}</StatNumber>
          <StatLabel>Follow-ups</StatLabel>
        </StatCard>
      </StatsContainer>

      {filteredAutomations.length === 0 ? (
        <EmptyState>
          <EmptyIcon>🤖</EmptyIcon>
          <h3>
            {searchTerm || filterStatus !== "all"
              ? "Nenhuma automação encontrada"
              : "Nenhuma automação criada"}
          </h3>
          <p>
            {searchTerm
              ? "Tente ajustar sua busca ou filtros."
              : "Comece criando sua primeira automação de mensagens."}
          </p>
          {!searchTerm && filterStatus === "all" && (
            <Button
              variant="primary"
              onClick={() => {
                if (isInsideAgentHub) {
                  navigate(`/agent-hub/${agentId}/automations/create`);
                } else {
                  navigate("/automations/create");
                }
              }}
            >
              Criar Primeira Automação
            </Button>
          )}
        </EmptyState>
      ) : (
        <CardsContainer>
          {filteredAutomations.map((automation) => (
            <AutomationCard
              key={automation.id}
              onClick={() => handleCardClick(automation)}
            >
              <CardHeader>
                <CardTitle>{automation.name}</CardTitle>
                <StatusBadge active={automation.active}>
                  {automation.active ? "Ativa" : "Inativa"}
                </StatusBadge>
              </CardHeader>

              <CardContent>
                {automation.description && (
                  <Description>{automation.description}</Description>
                )}

                <InfoRow>
                  <InfoLabel>Tipo:</InfoLabel>
                  <InfoValue>
                    <TypeBadge automationType={automation.automation_type || 'whatsapp'}>
                      {automation.automation_type || 'whatsapp'}
                    </TypeBadge>
                  </InfoValue>
                </InfoRow>

                <InfoRow>
                  <InfoLabel>Regras:</InfoLabel>
                  <InfoValue>{automation.rules_count || 0}</InfoValue>
                </InfoRow>
              </CardContent>

              <CardFooter>
                <UpdateDate>
                  {automation.created_at &&
                    new Date(automation.created_at).toLocaleString("pt-BR")}
                </UpdateDate>

                <ActionsContainer>
                  <ActionButton
                    variant="toggle"
                    active={automation.active}
                    title={automation.active ? "Desativar" : "Ativar"}
                    onClick={(e) => handleToggleActive(automation.id, e)}
                  >
                    <IconPower size={16} />
                  </ActionButton>

                  <ActionButton
                    variant="edit"
                    title="Editar"
                    onClick={(e) => handleEdit(automation, e)}
                  >
                    <IconPencil size={16} />
                  </ActionButton>

                  <ActionButton
                    variant="duplicate"
                    title="Duplicar"
                    onClick={(e) => handleDuplicate(automation.id, e)}
                  >
                    <IconCopy size={16} />
                  </ActionButton>

                  <ActionButton
                    variant="delete"
                    title="Excluir"
                    onClick={(e) =>
                      handleDelete(automation.id, automation.name, e)
                    }
                  >
                    <IconTrash size={16} />
                  </ActionButton>
                </ActionsContainer>
              </CardFooter>
            </AutomationCard>
          ))}
        </CardsContainer>
      )}

      <FloatingButton
        onClick={() => {
          navigate(`/agent-hub/${agentId}/automations/create`);
        }}
        title="Nova Automação"
      >
        <IconPlus size={24} />
      </FloatingButton>
    </Container>
  );
};

export default AutomationsList;
